import React, { createContext, useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import mitt from "mitt";

export const InnerContext = createContext({});

export const EmitterEvents = {
  valuesChange: "valuesChange"
};

const FormInner = React.forwardRef(
  (
    { children, action, form, disabled, className, style, onValuesChange },
    ref
  ) => {
    const innerContextRef = useRef({});
    const formikProps = useFormikContext();
    const valuesRef = useRef(formikProps.initialValues);

    const formikSetFieldValue = formikProps.setFieldValue;

    const triggerValuesChange = (changedValues, allValues) => {
      onValuesChange?.(changedValues, allValues);
      innerContextRef.current.emitter.emit(
        EmitterEvents.valuesChange,
        changedValues
      );
    };

    const getFieldValue = field => {
      return formikProps.values[field];
    };

    const getFieldsValue = () => {
      return formikProps.values;
    };

    const getFieldPrevValue = field => {
      return valuesRef.current[field];
    };

    const getFieldsPrevValue = () => {
      return valuesRef.current;
    };

    const setFieldValue = (field, value, shouldValidate) => {
      valuesRef.current[field] = value;
      triggerValuesChange({ [field]: value }, valuesRef.current);
      return formikSetFieldValue(field, value, shouldValidate);
    };

    const setFieldsValue = values => {
      return formikProps.resetForm({ values });
    };

    innerContextRef.current.form = formikProps;
    innerContextRef.current.form.getFieldValue = getFieldValue;
    innerContextRef.current.form.getFieldsValue = getFieldsValue;
    innerContextRef.current.form.setFieldValue = setFieldValue;
    innerContextRef.current.form.setFieldsValue = setFieldsValue;

    innerContextRef.current.getFieldPrevValue = getFieldPrevValue;
    innerContextRef.current.getFieldsPrevValue = getFieldsPrevValue;
    innerContextRef.current.disabled = disabled;

    if (!innerContextRef.current.emitter) {
      innerContextRef.current.emitter = mitt();
      innerContextRef.current.form.emitter = innerContextRef.current.emitter;
    }

    if (form && "current" in form) {
      form.current = innerContextRef.current.form;
      form.current.emitter = innerContextRef.current.emitter;
    }

    useEffect(() => {
      const values = formikProps.values;
      const changedValues = {};
      let changed = false;

      Object.keys(values).forEach(field => {
        if (valuesRef.current[field] !== values[field]) {
          changedValues[field] = values[field];
          changed = true;
        }
      });

      if (changed) {
        triggerValuesChange(changedValues, values);
      }

      valuesRef.current = values;
    }, [formikProps.values]);

    useEffect(() => {
      return () => {
        innerContextRef.current.emitter.all.clear();
      };
    }, []);

    return (
      <InnerContext.Provider value={innerContextRef.current}>
        <form
          onSubmit={formikProps.handleSubmit}
          ref={ref}
          onReset={formikProps.handleReset}
          action={action}
          className={className}
          style={style}
        >
          {children}
        </form>
      </InnerContext.Provider>
    );
  }
);

export default FormInner;
